<template>
  <BbTextarea
    ref="inputField"
    variant="underlined"
    v-model="inputValue"
    :placeholder="t(placeholderTextKey)"
    :rows="rows"
    :max-rows="10"
    :auto-grow="true"
    :class="[!!summarization || !!remixImage ? 'bb-textarea--summarized' : 'bb-textarea']"
    :disabled="isDisabled"
    @keydown="handleKeyDown"
  >
    <template #append-inner>
      <BbBtn
        size="x-small"
        color="#6222e3"
        :is-disabled="isTyping || isDisabled"
        @click="handleSubmit"
        :width="36"
        :height="36"
      >
        <BbIcon size="small" name="arrow-up" />
      </BbBtn>
    </template>
    <template #prepend-inner>
      <template v-if="!!remixImage">
        <div class="remix-image-preview">
          <img :src="remixImage" alt="Remix image" class="remix-thumbnail" />
        </div>
        <BbBtn
          size="x-small"
          type="icon"
          icon="x-small"
          icon-size="medium"
          no-background
          @click="handleClearRemixImage"
        />
      </template>
      <template v-else-if="!!summarization">
        <div>{{ summarization }}</div>
        <BbBtn
          v-if="selectedElementText"
          size="x-small"
          type="icon"
          icon="x-small"
          icon-size="medium"
          no-background
          @click="handleHideSummarization"
        />
      </template>
      <SelectedFiltersComponent v-else />
    </template>
  </BbTextarea>
</template>

<script>
import { ref, watch, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useWesley } from '@anthill-modules/wesley-components';

import { SetQueryValue } from '~/constants/mutations';
import { Routes } from '~/constants/routes';

import useConversations from '~/composables/useConversations';
import useDocumentEditor from '~/composables/useDocumentEditor';
import useAuthoring from '~/composables/useAuthoring';
import useFormFilters from '~/composables/useFormFilters';
import useQuickPrompts from '~/composables/useQuickPrompts';
import useImageGeneration from '~/composables/useImageGeneration';
import SelectedFiltersComponent from '~/services/filter-form-service/components/SelectedFiltersComponent.vue';

export default {
  components: { SelectedFiltersComponent },
  props: {
    submitText: {
      type: String,
      default: 'Submit',
    },
    placeholderTextKey: {
      type: String,
      default: 'conversation.typeAMessage',
    },
    modelValue: {
      type: String,
      default: '',
    },
    rows: {
      type: Number,
      default: 1,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'onSubmit'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const route = useRoute();
    const { isAuthoringStarted } = useAuthoring();
    const isAuthoringRoute = computed(() => route.name === Routes.Authoring.name);
    const { isTyping, currentConversation } = useConversations();
    const { selectedObjectDocumentEditor } = useDocumentEditor();
    const { searchQuery } = useFormFilters();
    const { sendMessage: sendMessageToDocument } = useWesley();
    const { setCurrentPromptCard } = useQuickPrompts();
    const { setUserImageForRemix, remixImage: remixImageSObj } = useImageGeneration();

    const inputValue = ref(props.modelValue || '');
    const inputField = ref(null);
    const remixImage = ref(null);
    const selectedElementText = computed(
      () =>
        selectedObjectDocumentEditor?.value?.selectedElement?.element?.text ||
        selectedObjectDocumentEditor?.value?.selectedElement?.attributes?.fieldName || // if a whole layout is selected, summarize all the text elements
        selectedObjectDocumentEditor?.value?.selectedLayout?.children
          ?.map((child) => child.element?.text || child.attributes?.fieldName)
          .join('\n\n'),
    );
    const summarization = computed(
      () =>
        isAuthoringRoute.value &&
        isAuthoringStarted.value &&
        (selectedElementText.value || t('author.promptEntireDocument')),
    );

    const handleSubmit = () => {
      if (props.isDisabled) return;

      if (!isTyping.value && inputValue.value && inputValue.value.trim() !== '') {
        setCurrentPromptCard(null); //make sure is no remaining active card value when sending assist message
        store.commit(`conversationStore/${SetQueryValue}`, '');
        emit('onSubmit');
        inputValue.value = '';
        remixImage.value = null;
      }
    };

    const handleKeyDown = (event) => {
      if (props.isDisabled) return;

      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSubmit();
      }
    };

    const handleHideSummarization = () => {
      sendMessageToDocument({ method: 'deselectElement' });
    };

    const handleClearRemixImage = () => {
      remixImage.value = null;
      setUserImageForRemix(null);
    };

    // Watch for changes in the store and update the local value
    watch(searchQuery, (newValue) => {
      inputValue.value = newValue;
    });

    // Watch for changes in the store and update the local value
    watch(
      () => store.state.conversationStore.queryValue,
      (newValue) => {
        if (typeof newValue === 'object' && newValue !== null && remixImageSObj.value) {
          inputValue.value = newValue?.query?.replace(/<[^>]*>/g, '') || '';
          remixImage.value = newValue?.remixImage || null;
        } else {
          // Stored query text can carry markup (e.g. <p>...</p>) — strip it here too,
          // same as the remix branch above, so a restored query renders as plain text.
          inputValue.value = (newValue || '').replace(/<[^>]*>/g, '');
          remixImage.value = null;
        }
      },
      { immediate: true },
    );

    // Watch for changes in the local value and update the store
    watch(
      () => inputValue.value,
      (newValue) => {
        store.dispatch(`formFiltersStore/${SetQueryValue}`, newValue);
        emit('update:modelValue', newValue);
      },
    );

    // focus on input each time you change conversation or create a new one
    watch(
      () => currentConversation.value.conversationId,
      () => {
        if (!props.isDisabled) {
          inputField.value.$el.querySelector('textarea').focus();
        }
      },
    );

    // make sure the focus is on input when you are on route /assist (on new conversation and refresh page or change route)
    onMounted(() => {
      if (!props.isDisabled) {
        inputField.value.$el.querySelector('textarea').focus();
      }
    });

    return {
      inputValue,
      isTyping,
      handleKeyDown,
      handleSubmit,
      handleHideSummarization,
      handleClearRemixImage,
      summarization,
      remixImage,
      currentConversation,
      t,
      inputField,
      selectedElementText,
    };
  },
};
</script>

<style lang="scss" scoped>
.bb-textarea {
  :deep(.v-input .v-input__control .v-field) {
    border-radius: 24px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-2;

    .v-field__append-inner {
      align-items: flex-end;
    }

    .v-field__field {
      textarea {
        @include bb-regular-text-styles('lg');
      }
    }
  }
}

.bb-textarea--summarized {
  :deep(.v-input .v-input__control .v-field) {
    min-height: 133px;
    width: 100%;
    display: flex;
    flex-direction: column;
    border-radius: 24px;

    .v-field__append-inner {
      position: absolute;
      right: 12px;
      bottom: 0;
    }

    .v-field__field {
      textarea {
        @include bb-regular-text-styles('lg');
      }
    }

    .v-field__prepend-inner {
      background-color: $bb-neutral-6;
      flex: 1;
      border-radius: 16px;
      margin-top: 10px;
      padding: 14px 11px;
      font-size: 15px;
      color: $bb-neutral-2;
      display: flex;
      align-items: center;
      justify-content: space-between;

      &:has(img) {
        width: 100px;
        align-items: flex-start;
      }

      div {
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      .remix-image-preview {
        display: flex;
        align-items: center;
        gap: $bb-spacing-2;
        flex: 1;
        overflow: hidden;

        .remix-thumbnail {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          object-fit: cover;
          flex-shrink: 0;
        }

        .remix-query {
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
      }
    }
  }
}
</style>
